import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { AuthShell } from '@/components/Auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CountryCodeSelect from '@/components/ui/CountryCodeSelect';
import { DEFAULT_COUNTRY_CODE } from '@/config';
import useLogin from '@/hooks/useLogin';
import { checkPhoneNumberValidation, convertArabicToEnglishNumbers } from '@/utils';

const REGISTER_URL = 'https://tryboxy.com/registration-form/?source=merchant_dashboard';

const Login = () => {
  const { t } = useTranslation();
  const { onFinish, isLoading } = useLogin();

  // Stored without the leading '+' — that is the shape every phone helper and
  // the login payload expect. The select is the only place it wears a plus.
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);

  /*
   * 🔴 WHY AM I LOOKING AT A LOGIN PAGE? (QA D8.)
   *
   * A member whose access to their active workspace is withdrawn — removed from
   * the team, or the workspace deleted — has a token naming a workspace they
   * are no longer in. The API answers `WORKSPACE_FORBIDDEN` and the interceptor
   * signs them out, which is right. Landing on a bare login form with no
   * explanation is not: they were working a second ago and nothing says what
   * happened, so the natural reading is "the app is broken".
   *
   * Read once and cleared, so it explains this arrival and not the next one.
   */
  /*
   * ⚠️ THE INITIALISER ONLY READS. CLEARING IS AN EFFECT, AND THAT DISTINCTION
   * IS WHY THIS DID NOT RENDER (QA round 4).
   *
   * The first version deleted the key inside the `useState` initialiser. The
   * app runs under `React.StrictMode`, which DOUBLE-INVOKES initialisers to
   * surface exactly this kind of impurity: the first call read the notice and
   * removed it, the second read `null` and returned false — and the second
   * result is the one React keeps. The notice was consumed before it could be
   * shown.
   *
   * ⭐⭐⭐ A useState INITIALISER MUST BE PURE. A side effect hidden in one is
   * invisible in review, works in isolation, and disappears under StrictMode —
   * which is the mode the app actually runs in.
   *
   * So: read here, clear in an effect. The notice explains THIS arrival and not
   * the next one, and a reload before the effect runs simply shows it again,
   * which is harmless.
   */
  const [accessNotice] = useState(() => {
    try {
      return sessionStorage.getItem('auth_notice') === 'workspace_access_lost';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!accessNotice) return;

    try {
      sessionStorage.removeItem('auth_notice');
    } catch {
      // Private mode / blocked storage. Showing the sentence twice is a far
      // smaller problem than not showing it at all.
    }
  }, [accessNotice]);

  const isValid = checkPhoneNumberValidation(phone, countryCode);
  // Don't scold someone who is still typing — only once they've tried to submit.
  const showError = touched && !isValid;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!isValid || isLoading) return;
    onFinish({ phoneNumber: phone, countryCode });
  };

  return (
    <AuthShell
      footer={
        <span className='text-sd-muted-foreground'>
          {String(t('auth_no_account'))}{' '}
          <a
            href={REGISTER_URL}
            target='_blank'
            rel='noreferrer'
            className='font-semibold text-sd-link hover:underline'
          >
            {String(t('auth_create_account'))}
          </a>
        </span>
      }
    >
      <h1 className='text-[26px] font-extrabold leading-tight tracking-[-0.01em] text-sd-foreground'>
        {String(t('auth_welcome_title'))}
      </h1>
      <p className='mt-2 text-[15px] leading-relaxed text-sd-muted-foreground'>
        {String(t('auth_welcome_subtitle'))}
      </p>

      {accessNotice ? (
        // Informational, not an error: nothing has gone wrong with their
        // account, and signing in again lands them on a workspace they DO
        // belong to.
        <p
          role='status'
          className='mt-4 rounded-lg border border-sd-border bg-sd-muted px-3 py-2 text-[13px] leading-relaxed text-sd-foreground'
        >
          {String(t('auth_workspace_access_lost'))}
        </p>
      ) : null}

      <form onSubmit={handleSubmit} noValidate className='mt-7'>
        <label htmlFor='phone' className='mb-2 block text-sm font-semibold text-sd-foreground'>
          {String(t('auth_phone_label'))}
        </label>

        {/* Phone numbers read left-to-right in every locale, so this row stays
            LTR while the page around it flips. */}
        <div dir='ltr' className='flex items-center gap-2'>
          <CountryCodeSelect
            className='h-12 rounded-xl'
            value={`+${countryCode}`}
            onChange={(value) => setCountryCode(value.replace('+', ''))}
          />
          <Input
            id='phone'
            name='phone'
            type='tel'
            inputMode='numeric'
            autoComplete='tel'
            autoFocus
            dir='ltr'
            placeholder='7XX XXX XXXX'
            value={phone}
            // Arabic keyboards emit Arabic-Indic digits; normalise before they
            // ever reach validation or the payload.
            onChange={(event) =>
              setPhone(convertArabicToEnglishNumbers(event.target.value).replace(/\D/g, ''))
            }
            aria-invalid={showError}
            className={`h-12 flex-1 rounded-xl text-base tracking-[0.03em] ${
              showError ? 'border-sd-destructive focus-visible:ring-sd-destructive' : ''
            }`}
          />
        </div>

        {showError ? (
          <p role='alert' className='mt-2 text-[13px] font-medium text-sd-destructive'>
            {String(t(phone ? 'invalid_phone_number' : 'phone_number_is_required'))}
          </p>
        ) : null}

        <Button
          type='submit'
          disabled={isLoading}
          className='group mt-6 h-12 w-full rounded-xl text-[15px] font-bold'
        >
          {isLoading ? <Loader2 className='animate-spin' /> : null}
          {String(t('auth_continue'))}
          <ArrowRight className='transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5' />
        </Button>
      </form>

      <p className='mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-sd-muted-foreground'>
        <ShieldCheck className='h-3.5 w-3.5 shrink-0' strokeWidth={2} />
        {String(t('auth_secure_note'))}
      </p>
    </AuthShell>
  );
};

export default Login;
